import React, { useCallback, useEffect, useState } from 'react';
import { Descriptions, InputNumber, Input, Select, Button, Radio, Space, Tag, Tooltip, Popconfirm, Table, Collapse, message } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import apiClient from '../../../shared/services/apiClient';

const MODES = [
    { value: 'grant', label: '赠送本期' },
    { value: 'permanent', label: '永久赠送' },
    { value: 'charge', label: '扣余额' },
];
const MODE_TIP = '赠送本期：现在免费，续费照收；永久赠送：续费也不收；扣余额：同客户购买';

const call = async (method, url, data) => {
    try {
        return (await apiClient[method](url, data)).data;
    } catch (e) {
        return { status: 'error', msg: e.response?.data?.msg || e.message };
    }
};

/** 后台独享容器「附加」：加磁盘 / 独立公网 IP / 端口映射。 */
const AdminContainerExtras = ({ id, onChanged }) => {
    const [d, setD] = useState(null);
    const [busy, setBusy] = useState(null);
    const [mode, setMode] = useState('grant');
    const [gb, setGb] = useState(10);
    const [port, setPort] = useState({ protocol: 'tcp', internal_port: null, external_port: null, description: '' });
    const base = `/api/evm/admin/containers/${id}/extras`;

    const load = useCallback(async () => {
        const r = await call('get', base);
        if (r?.status === 'success') setD(r.data); else message.error(r?.msg || '加载失败');
    }, [base]);

    useEffect(() => { if (id) load(); }, [id, load]);

    const run = async (key, url, data) => {
        setBusy(key);
        const r = await call('post', url, data);
        setBusy(null);
        if (r?.status === 'success') {
            const x = r.data || {};
            if (x.pending) message.warning('已赠送，节点资源不足，待开通');
            else if (mode === 'charge' && x.invoice_id) message.success(x.paid ? `已扣余额（账单 #${x.invoice_id}）` : `余额不足，已生成待付账单 #${x.invoice_id}`);
            else message.success(r.msg || '已保存');
            load();
            onChanged && onChanged();
        } else message.error(r?.msg || '操作失败');
    };

    if (!d) return null;
    const ports = d.ports || [];

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Space>
                <Tooltip title={MODE_TIP}><Radio.Group size="small" optionType="button" options={MODES} value={mode} onChange={(e) => setMode(e.target.value)} /></Tooltip>
                {d.waiting?.length > 0 && <Tag color="orange">待开通 {d.waiting.length}</Tag>}
            </Space>
            <Descriptions column={1} size="small" bordered labelStyle={{ width: 110 }}>
                <Descriptions.Item label="磁盘">
                    <Space wrap>
                        <span>{d.disk_gb} GB{d.extra_disk_gb ? `（附加 ${d.extra_disk_gb}）` : ''}</span>
                        <InputNumber size="small" min={1} max={Math.max(1, d.max_disk_gb - d.disk_gb)} value={gb} onChange={setGb} addonBefore="+" addonAfter="GB" style={{ width: 150 }} />
                        <Button size="small" type="primary" loading={busy === 'disk'} disabled={d.disk_gb >= d.max_disk_gb}
                            onClick={() => run('disk', `${base}/disk`, { gb, mode })}>添加</Button>
                        <span style={{ color: '#999', fontSize: 12 }}>上限 {d.max_disk_gb} GB</span>
                    </Space>
                </Descriptions.Item>
                <Descriptions.Item label="独立公网 IP">
                    <Space wrap>
                        {(d.eips || []).map((e) => (
                            <Tag key={e.id}>
                                {e.public_ip}
                                <Popconfirm title="释放该 IP？" onConfirm={() => run(`rel${e.id}`, `${base}/eip/${e.id}/release`)}>
                                    <DeleteOutlined style={{ marginLeft: 6, cursor: 'pointer' }} />
                                </Popconfirm>
                            </Tag>
                        ))}
                        {!(d.eips || []).length && (
                            <Button size="small" type="primary" loading={busy === 'eip'} onClick={() => run('eip', `${base}/eip`, { mode })}>添加</Button>
                        )}
                    </Space>
                </Descriptions.Item>
            </Descriptions>
            <Table size="small" rowKey="id" pagination={false} dataSource={ports}
                title={() => <span>端口映射 <span style={{ color: '#999', fontSize: 12 }}>{ports.length} / {d.max_ports}</span></span>}
                locale={{ emptyText: '无' }}
                columns={[
                    { title: '协议', dataIndex: 'protocol', width: 70 },
                    { title: '外部', dataIndex: 'external_port', width: 90 },
                    { title: '容器', dataIndex: 'internal_port', width: 90 },
                    { title: '状态', dataIndex: 'status', width: 90, render: (s) => <Tag>{s}</Tag> },
                    { title: '备注', dataIndex: 'description', ellipsis: true },
                    {
                        title: '', key: 'op', width: 50, render: (_, p) => p.status !== 'deleting' && (
                            <Popconfirm title="删除该映射？" onConfirm={() => run(`pd${p.id}`, `${base}/ports/${p.id}/delete`)}>
                                <Button size="small" type="text" danger icon={<DeleteOutlined />} loading={busy === `pd${p.id}`} />
                            </Popconfirm>
                        ),
                    },
                ]} />
            <Space wrap>
                <Select size="small" value={port.protocol} onChange={(v) => setPort({ ...port, protocol: v })} style={{ width: 80 }}
                    options={[{ value: 'tcp', label: 'TCP' }, { value: 'udp', label: 'UDP' }]} />
                <InputNumber size="small" min={1} max={65535} placeholder="容器端口" value={port.internal_port} onChange={(v) => setPort({ ...port, internal_port: v })} style={{ width: 110 }} />
                <Button size="small" type="primary" loading={busy === 'port'} disabled={!port.internal_port}
                    onClick={() => run('port', `${base}/ports`, port)}>添加映射</Button>
            </Space>
            <Collapse ghost size="small" items={[{
                key: 'adv', label: '高级',
                children: (
                    <Space wrap>
                        <Tooltip title="留空自动分配">
                            <InputNumber size="small" min={1} max={65535} placeholder="外部端口" value={port.external_port} onChange={(v) => setPort({ ...port, external_port: v })} style={{ width: 110 }} />
                        </Tooltip>
                        <Input size="small" placeholder="备注" value={port.description} onChange={(e) => setPort({ ...port, description: e.target.value })} style={{ width: 200 }} />
                    </Space>
                ),
            }]} />
        </Space>
    );
};

export default AdminContainerExtras;
